// @flow
import React from 'react';
import classnames from 'classnames';
import { get } from 'lodash';

import * as routeBuilder from 'Routes/routeBuilder';
import { ICONS } from 'Components/utils/icon/icons';
import signoutAction from 'Store/user-info/actions/signout';
import * as pleUtils from 'Utils/ple-utils';
import { clearAllCacheFront } from 'Utils/graphql-client/cacheFront';
import { useTracking } from 'Tracking/tracking-api/useTracking';

import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';

type Props = {
  bookingId: string,
  cmsContent: CMSContent,
  stayId: string,
};

type Item = {
  dataTestId?: string,
  description?: string,
  icon: IconName,
  hide?: boolean,
  isExternalLink?: boolean,
  label: string,
  onClick?: () => void,
  target?: string,
  url: ?string,
};

export function QuickLoggedNav({ cmsContent, bookingId, stayId }: Props) {
  const track = useTracking({
    categoryGA: 'headerMenu',
    actionGA: 'header_memberSpace',
  });

  const items = [
    {
      label: get(cmsContent, 'navigationSections.dashboard', ''),
      url: routeBuilder.urlPattern('account'),
      dataTestId: 'member-nav-quickdashboard-dashboard',
      icon: ICONS.NOTEBOOK,
      onClick: () => {
        track({
          labelGA: 'memberSpace_dashboard',
        });
      },
    },
    {
      label: get(cmsContent, 'navigationSections.quickLogin.details', ''),
      url: routeBuilder.buildUrl('bookings', { bookingId, stayId }),
      dataTestId: 'member-nav-quickdashboard-details',
      icon: ICONS.DETAILS,
      hide: !bookingId || !stayId,
      onClick: () => {
        track({
          labelGA: 'memberSpace_bookingDetails',
        });
      },
    },
    {
      label: get(cmsContent, 'navigationSections.quickLogin.prepareMyStay', ''),
      url: routeBuilder.buildUrl('prepareMyStay', { bookingId, stayId }),
      dataTestId: 'member-nav-quickdashboard-prepareMyStay',
      icon: ICONS.LUGGAGE,
      hide: !bookingId || !stayId,
      onClick: () => {
        track({
          labelGA: 'memberSpace_prepareMyStay',
        });
      },
    },
    {
      label: get(cmsContent, 'navigationSections.quickLogin.signin', ''),
      url: routeBuilder.urlPattern('stepup'),
      dataTestId: 'member-nav-quickdashboard-stepup',
      icon: ICONS.CONNECT,
      description: get(cmsContent, 'navigationSections.quickLogin.signinDescription', ''),
      isExternalLink: true,
      onClick: () => {
        track({
          labelGA: 'memberSpace_stepUp',
        });
        clearAllCacheFront();
      },
    },
    {
      label: get(cmsContent, 'navigationSections.quickLogin.signout', ''),
      url: pleUtils.buildSignoutUrl(),
      dataTestId: 'member-nav-quickdashboard-Signout',
      icon: ICONS.DISCONNECTION,
      onClick: () => {
        clearAllCacheFront();
        signoutAction();
        track({
          labelGA: 'memberSpace_signOut',
        });
      },
      isExternalLink: true,
    },
  ];

  return (
    <nav>
      {items
        .filter((i: Item) => !i.hide)
        .map(
          (
            { dataTestId, description, icon, isExternalLink, label, onClick, target, url }: Item,
            index: number,
          ) => (
            <div className={classnames('p-3', { 'border-t-1': index > 0 })} key={url}>
              {description && (
                <p className="reset-paragraph text-xs text-gray-darker">{description}</p>
              )}
              <DynamicLink
                className="flex items-center font-bold leading-tight text-gray-darker text-md focus:text-blue hover:text-blue transition-color"
                data-testid={dataTestId}
                isExternalLink={isExternalLink}
                onClick={onClick}
                target={target}
                url={url}
              >
                {icon && <Icon className="rtl:mr-3" name={icon} width="1.25rem" />}
                {label}
              </DynamicLink>
            </div>
          ),
        )}
    </nav>
  );
}
